import { useState, useEffect } from 'react'
import { useInViewOnce } from '@helpers/useIntersectionObserver'
import {ROUTES } from '@/constants'
import Button from '@ui/Button'
import Industries from '@components/Industries'
import { useTypewriter } from '../../assets/js/common'

const IMPACT_STATS = [
  { id: 'experience',     target: 16, suffix: '+', label: 'Years of Existance' },
  { id: 'projects', target: 200,  suffix: '+', label: 'Projects Completed' },
  { id: 'satisfaction',   target: 99,  suffix: '%', label: 'Satisfied Customers' },
]

// Counts up from 0 → target once the stat scrolls into view. Self-contained
// (own IntersectionObserver) so each stat triggers independently; fires only once.
function ImpactStatCounter({ target, suffix, label, duration = 1600 }) {
  const [value, setValue] = useState(0)

  const animateCount = () => {
    const start = performance.now()
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3) // ease-out cubic
      setValue(Math.round(eased * target))
      if (progress < 1) requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
  }

  const ref = useInViewOnce(animateCount, { threshold: 0.4 }, [target, duration])

  return (
    <div className="impact-stat" ref={ref}>
      <span className="impact-stat-value">{value}{suffix}</span>
      <span className="impact-stat-label">{label}</span>
    </div>
  )
}

// const TYPEWRITER_WORDS = ['AI Tools', 'visitors', 'Documentation', 'Infrastructure', 'APIs']
const TYPEWRITER_WORDS = ['AUSTRALIA','DUBAI', 'USA','INDIA']
const TYPING_SPEED = 90      // ms per character while typing
const ERASING_SPEED = 45     // ms per character while erasing
const PAUSE_AFTER_TYPE = 1400 // ms to hold the full word before erasing
const PAUSE_AFTER_ERASE = 400 // ms pause on empty string before next word starts

// function useTypewriter(words) {
//   const [wordIndex, setWordIndex] = useState(0)
//   const [text, setText] = useState('')
//   const [phase, setPhase] = useState('typing') // 'typing' | 'pausing' | 'erasing' | 'waiting'

//   useEffect(() => {
//     const currentWord = words[wordIndex]
//     let timer

//     if (phase === 'typing') {
//       if (text.length < currentWord.length) {
//         timer = setTimeout(() => setText(currentWord.slice(0, text.length + 1)), TYPING_SPEED)
//       } else {
//         timer = setTimeout(() => setPhase('pausing'), PAUSE_AFTER_TYPE)
//       }
//     } else if (phase === 'pausing') {
//       timer = setTimeout(() => setPhase('erasing'), 0)
//     } else if (phase === 'erasing') {
//       if (text.length > 0) {
//         timer = setTimeout(() => setText(currentWord.slice(0, text.length - 1)), ERASING_SPEED)
//       } else {
//         timer = setTimeout(() => setPhase('waiting'), PAUSE_AFTER_ERASE)
//       }
//     } else if (phase === 'waiting') {
//       timer = setTimeout(() => {
//         setWordIndex(prev => (prev + 1) % words.length)
//         setPhase('typing')
//       }, 0)
//     }

//     return () => clearTimeout(timer)
//   }, [text, phase, wordIndex, words])

//   return text
// }

// Same soft circular icon-badge treatment as the reference screenshot —
// light tinted background, line-style Font Awesome icon.
const ABOUT_SERVICES = [
  { id: '1',      icon: 'fa-solid fa-triangle-exclamation', label: 'Your IT agency does not understand your business.' },
  { id: '2',      icon: 'fa-solid fa-triangle-exclamation', label: 'Discussed something & developed something else.' },
  { id: '3',  icon: 'fa-solid fa-triangle-exclamation', label: 'You ended up spoon-feeding the developers all the time.' },
  { id: '4', icon: 'fa-solid fa-triangle-exclamation', label: 'Bug free delivery ! You might be kidding me.' },
  { id: '5',   icon: 'fa-solid fa-triangle-exclamation', label: 'The hosting budget comes up as a big surprise.' },
  { id: '6',    icon: 'fa-solid fa-triangle-exclamation', label: 'Proactive support and backup is only on paper.' },
]

/* ── Decorative floating heart icon ──
   Simple line/outline heart (stroke only), matching the minimal
   line-art style used elsewhere on the site. Purely decorative — the
   whole layer is aria-hidden, so it adds nothing for screen readers. */
// function HeartIcon({ className, style }) {
//   return (

//      <svg className={className} style={style} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
//       <path
//         d="M12 21s-6.7-4.35-9.3-8.28C.6 9.86 1.3 6.2 4.4 4.9c2.1-.9 4.2 0 5.6 1.9C11.4 4.9 13.5 4 15.6 4.9c3.1 1.3 3.8 5 1.7 7.82C18.7 16.65 12 21 12 21z"
//         fill="currentColor"
//       />
//     </svg>
//   )
// }

// Scattered across the left/right whitespace gutters — `pos` is a
// left/right offset (%) depending on `side`, `top` is vertical position
// (%) relative to the whole section. Varying size/duration/delay per
// heart keeps the float animation from looking synchronized.
const FLOATING_HEARTS = [
  { side: 'left',  top: '28%',  pos: '6%',  size: 22, tone: 'brand',     duration: 5,   delay: 0    },
  { side: 'left',  top: '52%', pos: '2%',  size: 16, tone: 'secondary', duration: 4.5, delay: 1.2  },
  { side: 'left',  top: '72%', pos: '22%',  size: 16, tone: 'secondary', duration: 8.5, delay: 2.2  },
  { side: 'left',  top: '74%', pos: '9%',  size: 26, tone: 'brand',     duration: 2.5, delay: 0.6  },
  { side: 'right', top: '14%', pos: '5%',  size: 18, tone: 'secondary', duration: 7.5, delay: 0.3  },
  { side: 'right', top: '48%', pos: '10%', size: 24, tone: 'brand',     duration: 5,   delay: 1.6  },
  { side: 'right', top: '68%', pos: '25%', size: 24, tone: 'brand',     duration: 4,   delay: 1.6  },
  { side: 'right', top: '80%', pos: '3%',  size: 16, tone: 'secondary', duration: 5.8, delay: 0.9  },
]


// Placeholder content (matches the reference screenshot exactly) —
// swap for real CruxBytes copy whenever ready.
const COMPARISON_ROWS = [
  { against: "You tell us the UI that you prefer",ours: 'We selectively choose and present you the best UI to fit your needs' },
  { against: 'We will provide extensive user manuals for your team',ours: 'We personally train your team and handhold them' },
  { against: "We will develop based on what you spoke with us", ours: "We will draft your requirements and then start development" },
  { against: 'You will need a team of resources for each different tasks',   ours: 'We develop with minimum number of resources and more efficiently' },
  { against: "We will migrate your old data after go-live",ours: 'We run a test-migration before go-live' },
  { against: 'We use AI for ticket routing',          ours: 'Our AI actually fixes the problem' },
  { against: 'We’ll respond within 4 working hours for any bugs',ours: 'Most issues are fixed within 30 minutes' },
]
export default function OurImpact() {
   const typedWord = useTypewriter(TYPEWRITER_WORDS)
  return (
    <section id="stack-showcase" className="section-py impact-section" aria-labelledby="impact-heading">
      <div className="container impact-grid">
        <div className="stack-content" data-aos="fade-right">
          <span className="impact-eyebrow">WHY CRUXBYTES</span>
          <h2 id="impact-heading" >
            <span className="impact-heading-dark">Smarter Development, </span>
            <span className="impact-heading-light">Smoother Business.</span>
          </h2>
          <p className="impact-desc">
            CruxBytes is an AI-Native software development company headquartered in Mumbai, India since 2010, serving businesses globally across 
                  <span className="about-heading-typed">
                     {typedWord}<span className="about-typed-cursor" aria-hidden="true" />
                  </span>
          </p>
          <p>From startups to established enterprises, we help organizations streamline operations, automate business processes, and leverage technology to achieve sustainable growth.</p>
          <p>We combine technical excellence with strategic thinking to deliver measurable business value.</p>
          <div className="impact-stats">
            {IMPACT_STATS.map((stat) => (
              <ImpactStatCounter key={stat.id} target={stat.target} suffix={stat.suffix} label={stat.label} />
            ))}
          </div>
          
        </div>

        <div className="pf-orbit-wrap" data-aos="fade-left">
          <Industries />
        </div>
        
      </div>
       <div className="wcs-actions" style={{paddingTop:'0'}}>
            <Button href={ROUTES.OUR_LEGACY} variant="secondary" icon="fa-arrow-right" iconPos="right">
              Read more about us
            </Button>                      
      </div>
       <div className="container about-grid">
             
             
      <div className="about-content" data-aos="fade-right">
                {/* <span className="impact-eyebrow">About Us</span> */}
                <h2 id="about-us-heading" className="about-heading">
                  Why switch to CruxBytes?                
                </h2>
                <p className="section-subheading">
                  We have come across a lot of clients who have had a bad experience with other IT vendors and left their business stranded at crucial junctures. Some of the common issues being :
                </p>
                
              </div>
              <div className="about-services-grid py-4" data-aos="fade-left">
                {ABOUT_SERVICES.map(service => (
                  <div key={service.id} className="card card--row card--rounded-xl about-service-card">
                    <span className="icon-circle about-service-icon">
                      <i className={service.icon} aria-hidden="true" />
                    </span>
                    <span>{service.label}</span>
                  </div>
                ))}
              </div>
               
            </div> 
         
       <div className="comparison-section" aria-labelledby="comparison-heading"  >
      {/* <div className="comparison-hearts" aria-hidden="true">
        {FLOATING_HEARTS.map((h, i) => (
          <HeartIcon
            key={i}
            className={`comparison-heart comparison-heart--${h.tone}`}
            style={{
              top: h.top,
              [h.side]: h.pos,
              width: h.size,
              height: h.size,
              animationDuration: `${h.duration}s`,
              animationDelay: `${h.delay}s`,
            }}
          />
        ))}
      </div> */}
      <div className="container">
       <div className="comparison-intro" data-aos="fade-left">
  <div><p className="impact-desc comparison-intro-text">Most businesses switch to CruxBytes after one bad experience with a reactive provider. Let's make sure you don't need one to get started.</p></div>

  <div className="wcs-actions comparison-intro-cta"  data-aos="fade-right">
    <Button  href={ROUTES.SALES_ENQUIRY}  variant="secondary" icon="fa-arrow-right" iconPos="right">
      Talk To Sales
    </Button>
  </div>
</div>

        <div className="comparison-table" role="table" aria-label="Comparison of other providers versus us"  data-aos="fade-up">
          <div className="comparison-row comparison-row--head" role="row">
            <div className="comparison-cell comparison-cell--head comparison-cell--against" role="columnheader">
              <i className="fa-solid fa-xmark comparison-head-icon comparison-head-icon--against" aria-hidden="true" />
              What Other Agencies Say
            </div>
            <div className="comparison-cell comparison-cell--head comparison-cell--ours" role="columnheader">
              <i className="fa-solid fa-check comparison-head-icon comparison-head-icon--ours" aria-hidden="true" />
              What CruxBytes Delivers
            </div>
          </div>

          {COMPARISON_ROWS.map(row => (
            <div className="comparison-row" role="row" key={row.against}>
              <div className="comparison-cell comparison-cell--against" role="cell">
                <span className="comparison-strike">{row.against}</span>
              </div>
              <div className="comparison-cell comparison-cell--ours" role="cell">
                {row.ours}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
    </section>
    
  )
}